import React from 'react';
import { MarketingPage, MarketingContent } from '../components/MarketingPage';

const content: MarketingContent = {
  seo: {
    title: 'An iCloud Alternative That Keeps Your Files on Your Own Devices',
    description:
      'Tired of paying for iCloud storage? Local Area Cloud backs up your camera roll to your own PC over Wi-Fi and moves files across iPhone, Mac, Windows, Android and Linux — private, no cloud, about $10 a year.',
    path: '/icloud-alternative',
  },
  eyebrow: 'An iCloud alternative',
  h1: <>An iCloud alternative that <span className="em">keeps your files with you.</span></>,
  lead:
    'iCloud is convenient — until the free 5 GB fills, the upsell arrives, and you’re paying every month to keep your own photos on Apple’s servers. Local Area Cloud keeps them on your own devices instead, and works well beyond Apple — iPhone, Mac, Windows, Android and Linux, all together.',
  sections: [
    {
      h2: 'Why people look for an iCloud alternative',
      paras: [
        'The free 5 GB is shared across Backups, Photos, Mail and Drive, so it fills almost immediately — and a single iPhone backup can eat most of it. From there it’s $0.99, then $2.99, then $9.99 a month, forever, just to hold what’s already on your phone.',
        'And it’s an Apple-first world: getting your iCloud photos onto a Windows PC or an Android phone is clumsy at best. Local Area Cloud treats every device as a first-class citizen and keeps your data on hardware you own.',
      ],
    },
    {
      h2: 'iCloud+ vs Local Area Cloud',
      paras: [
        'The honest side-by-side, including the one thing iCloud still does better:',
      ],
      table: {
        head: ['', 'iCloud+', 'Local Area Cloud'],
        rows: [
          ['Free tier', '5 GB (shared with Backup & Mail)', { v: '2 devices, every feature', kind: 'yes' }],
          ['To keep ~2 TB', { v: '$9.99/mo — ~$120/yr', kind: 'no' }, { v: 'Your own disks — $0', kind: 'yes' }],
          ['Where files live', { v: 'Apple’s servers', kind: 'no' }, { v: 'Your devices', kind: 'yes' }],
          ['Works on Windows & Android', { v: 'Limited', kind: 'no' }, { v: 'Full native apps', kind: 'yes' }],
          ['Camera roll backed up to your own PC', { v: 'No', kind: 'no' }, { v: 'Yes — over Wi-Fi', kind: 'yes' }],
          ['Private — never scanned', { v: 'Scanned & indexed', kind: 'no' }, { v: 'Never reaches us', kind: 'yes' }],
          ['Reach devices away from home', 'Yes, built in', 'Your own VPN (Pro)'],
          ['Off-site copy if devices are lost', { v: 'Yes', kind: 'yes' }, 'Pair a device / keep one always-on'],
          ['Cost over 3 years (2 TB)', { v: '≈ $360', kind: 'no' }, { v: '≈ $30 Pro — or $0', kind: 'yes' }],
        ],
      },
    },
    {
      h2: 'Your iCloud storage is mostly your camera roll',
      paras: [
        'Look at what’s actually eating your iCloud plan: photos and videos. Local Area Cloud’s Automatic Backup mirrors your camera roll to your own PC over Wi-Fi while your phone charges — full resolution, nothing downscaled, no monthly fee. The single biggest reason you pay Apple for storage becomes something your own devices simply do overnight.',
      ],
    },
    {
      h2: 'Moving off iCloud without losing anything',
      paras: [
        'You don’t have to rip everything out at once. Keep Apple’s free 5 GB for device backups if you like, and let Local Area Cloud carry the heavy part — your photos and files — onto your own devices. Nothing is uploaded to us, nothing is scanned, and there’s no second copy living in an account you’ll forget to cancel.',
      ],
    },
    {
      h2: 'What about when I’m away from my devices?',
      paras: [
        'Pro includes a built-in WireGuard VPN. Bring your phone onto a secure tunnel to a server you control — your router, your home box — and reach your files, backups and messages from anywhere, still with no third-party cloud in the middle.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Can Local Area Cloud really replace iCloud?',
      a: 'For the big jobs — backing up your photos, moving files between your devices, and reaching them across platforms — yes, and without a monthly bill. The one thing iCloud still does better is keep an off-site copy if all of your devices are lost at once; you can cover that by keeping one always-on device.',
    },
    {
      q: 'Does it back up my iPhone photos?',
      a: 'Yes. Automatic Backup mirrors your camera roll to another of your own devices — for example your home PC — over Wi-Fi while your phone charges, at full quality.',
    },
    {
      q: 'Is it actually private?',
      a: 'Yes. Your file contents never reach our servers, so there is nothing on our side to scan, index, or hand over.',
    },
    {
      q: 'Do I still need to pay Apple?',
      a: 'No. Local Area Cloud is free for up to two devices; Pro is about $10 a year for unlimited devices and VPN reach. You can keep Apple’s free 5 GB alongside it if you want device backups there.',
    },
    {
      q: 'Does it work on Windows and Android too?',
      a: 'Yes — full native apps on Windows, macOS, Linux, iPhone and Android, all under one account.',
    },
  ],
  related: [
    { label: 'Compare all cloud storage', to: '/cloud-storage-alternative', note: 'iCloud, Google One, Dropbox & more' },
    { label: 'Transfer files between phone & PC', to: '/transfer-files-phone-to-pc', note: 'iPhone ⇄ Windows, no cable' },
    { label: 'File sharing without the cloud', to: '/file-sharing-without-the-cloud', note: 'How your files stay private' },
  ],
  ctaBand: {
    h2: 'Keep your photos. Drop the iCloud bill.',
    p: 'Free for two devices; about $10 a year for your whole fleet — and your files never leave your network.',
  },
};

export const ICloudAlternative: React.FC = () => <MarketingPage content={content} />;
export default ICloudAlternative;
